import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/motion";
import { api, imgSrc, pickLang } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const LOCALES = { it: "it-IT", en: "en-GB", es: "es-ES" };

const BlogPostPage = () => {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { t, lang } = useLang();

  useEffect(() => {
    setP(null);
    setNotFound(false);
    api.get(`/posts/${id}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-48 pb-32 text-center" data-testid="post-not-found">
        <p className="font-serif-d text-4xl mb-6">404</p>
        <Link to="/blog" className="text-black gold-underline">{t("blog.back")}</Link>
      </div>
    );
  }

  if (!p) {
    return <div className="pt-48 pb-32 text-center text-[#5B6670] font-light" data-testid="post-loading">{t("blog.loading")}</div>;
  }

  return (
    <div data-testid="blog-post-page" className="pt-36 sm:pt-40 pb-24">
      <Seo title={`${pickLang(p, "title", lang)} | ROMACASA Blog`} siteName="ROMACASA" description={pickLang(p, "excerpt", lang) || pickLang(p, "title", lang)} image={p.cover?.startsWith("/api") ? undefined : p.cover} />
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm text-[#5B6670] hover:text-black transition-colors mb-10">
          <ArrowLeft size={15} /> {t("blog.back")}
        </Link>
        <Reveal>
          <p className="font-mono-d text-xs text-[#8A94A0] mb-5">
            {new Date(p.created_at).toLocaleDateString(LOCALES[lang] || "it-IT", { day: "numeric", month: "long", year: "numeric" })}
          </p>
          <h1 className="font-serif-d text-4xl sm:text-5xl font-medium leading-tight mb-8">{pickLang(p, "title", lang)}</h1>
          {pickLang(p, "excerpt", lang) && <p className="font-serif-d italic text-xl sm:text-2xl text-[#5B6670] leading-relaxed mb-10">{pickLang(p, "excerpt", lang)}</p>}
          {p.cover && (
            <img src={imgSrc(p.cover)} alt={pickLang(p, "title", lang)} className="w-full aspect-[16/9] object-cover border border-[#E0E3E6] mb-10" data-testid="post-cover" />
          )}
          <div className="text-[#2B323A] font-light text-base sm:text-lg leading-[1.9] whitespace-pre-line" data-testid="post-content">
            {pickLang(p, "content", lang)}
          </div>
        </Reveal>
        <Reveal delay={0.1} className="mt-16 pt-10 border-t border-[#E0E3E6] flex justify-center">
          <img src="/icon-arch-dark.png" alt="ROMACASA" className="w-14 h-auto opacity-80" />
        </Reveal>
      </div>
    </div>
  );
};

export default BlogPostPage;
